import { useState } from 'react';
import Drawer from 'components/gruenland/atoms/drawer/Drawer';
import { Search } from 'components/gruenland/icons/Search';
import CartStatusIcon from 'components/gruenland/molecules/cart/CartStatusIcon';
import Slideout, { MenuState } from 'components/gruenland/organisms/header/utility-section/components/Slideout';
import WishlistIcon from 'components/icons/wishlist';
import { useFormat } from 'helpers/hooks/useFormat';
import useShopable from 'helpers/hooks/useShopable';
import { Cart, LineItem as CartLineItem, Discount } from 'types/entity/cart';
import { Wishlist, LineItem as WishlistLineItem } from 'types/entity/wishlist';
import AccountButton from './components/account-button';

export interface Props {
  cart?: Cart;
  isEmpty?: boolean;
  onApplyDiscountCode?: (code: string) => Promise<void>;
  onRemoveDiscountCode?: (discount: Discount) => Promise<void>;
  totalCartItems?: number;
  totalWishlistItems?: number;
  onRemoveItem(lineItem: CartLineItem): Promise<void>;
  onUpdateItem(itemId: string, quantity: number): Promise<void>;
  wishlist?: Wishlist;
  onRemoveFromWishlist?: (lineItemId: string) => Promise<void>;
  onMoveToCart?: (lineItem: WishlistLineItem) => Promise<void>;
  onClearWishlist?: () => Promise<void>;
  onSearchClick?: () => void;
}

const UtilitySection: React.FC<Props> = ({
  cart,
  isEmpty,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  onRemoveItem,
  onUpdateItem,
  totalCartItems = 0,
  totalWishlistItems = 0,
  wishlist,
  onRemoveFromWishlist,
  onMoveToCart,
  onClearWishlist,
  onSearchClick,
}) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [menuState, setMenuState] = useState<MenuState>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const shopable = useShopable();

  const onWishlistClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('wishlist');
  };

  const onCartClicked = () => {
    setIsDrawerOpen(true);
    setMenuState('cart');
  };

  return (
    <>
      <div className="flex items-center justify-between gap-x-2 md:gap-x-4">
        <button title={formatMessage({ id: 'search.title', defaultMessage: 'Cerca' })} onClick={onSearchClick}>
          <Search className="block" />
        </button>

        <AccountButton className="block" />

        <button
          title={formatMessage({ id: 'wishlist.title', defaultMessage: 'Lista dei desideri' })}
          className="relative hidden md:block"
          onClick={onWishlistClicked}
        >
          <WishlistIcon totalWishlistItems={totalWishlistItems} />
        </button>

        {shopable && (
          <button title={formatMessage({ id: 'cart.title', defaultMessage: 'Carrello' })} onClick={onCartClicked}>
            <CartStatusIcon totalCartItems={totalCartItems} />
          </button>
        )}
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        direction="right"
        className="w-[90%] max-w-[380px]"
        onClose={() => setIsDrawerOpen(false)}
      >
        <Slideout
          cart={cart}
          isEmpty={isEmpty}
          onApplyDiscountCode={onApplyDiscountCode}
          onRemoveDiscountCode={onRemoveDiscountCode}
          onRemoveItem={onRemoveItem}
          onUpdateItem={onUpdateItem}
          wishlist={wishlist}
          onRemoveFromWishlist={onRemoveFromWishlist}
          onMoveToCart={onMoveToCart}
          onClearWishlist={onClearWishlist}
          state={menuState}
          onClose={() => setIsDrawerOpen(false)}
          changeState={(newState) => setMenuState(newState)}
        />
      </Drawer>
    </>
  );
};

export default UtilitySection;
